import { useEffect, useMemo, useState } from 'react';
import {
	CONVERTED_IMAGE_STATUS_READY,
	canLookupConvertedImages,
} from '../thunks/converted_images_thunks';
import {
	PendingOriginal,
	findStoredRef,
	getOriginalKey,
	getStoreVersion,
	isStillConverting,
	subscribeToConvertedImageStatus,
} from './convertedImageStatusStore';

const decodeSegment = (segment: string): string => {
	try {
		return decodeURIComponent(segment);
	} catch (error) {
		return segment;
	}
};

// Mirrors `ImageConversionKeyUtil.fileIdFrom` on the backend: the fileId is the second-to-last
// path segment, and only when it looks like one. Taking the FIRST segment instead is wrong for the
// file-system bucket, whose keys start with the environment —
// `<env>/work_order_chat/<userType>/<companyId>/<woId>/<fileType>/<fileId>/<fileName>` — so a chat
// photo would report a fileId of "prod". The rule below is right for both bucket layouts.
const UPLOAD_FILE_ID_LENGTH = 50;

const getFileIdFromSegments = (segments: string[]): string | undefined => {
	if (segments.length < 2) {
		return undefined;
	}
	const candidate = segments[segments.length - 2];
	return candidate.length === UPLOAD_FILE_ID_LENGTH && /^[a-z0-9]+$/i.test(candidate)
		? candidate
		: undefined;
};

/**
 * The identity an absolute upload URL carries. The full path is always the object's key in its
 * bucket; the fileId is only present when the key follows the upload convention. Both are sent so
 * the lookup matches whichever column the conversion row was written with.
 */
export const getPendingOriginalFromUrl = (url: string): PendingOriginal | null => {
	if (!url) {
		return null;
	}
	const path = url
		.split('?')[0]
		.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '')
		.replace(/^\/+/, '');
	const segments = path.split('/').map(decodeSegment).filter(Boolean);
	if (segments.length === 0) {
		return null;
	}
	return {
		fileId: getFileIdFromSegments(segments),
		s3ObjectKey: segments.join('/'),
	};
};

export interface ConvertedImageStatusResult {
	/** True while at least one of this caller's originals is still being converted. */
	isAnyConverting: boolean;
	/** True while this original's derivative is still expected but not ready. */
	isConverting: (original: PendingOriginal | null) => boolean;
	/** The ready derivative's URL, or null when there is no usable conversion (yet). */
	getConvertedUrl: (original: PendingOriginal | null) => string | null;
}

// Reported where no lookup is possible, so the caller decodes locally instead of holding a
// placeholder for an answer that can never arrive.
const INERT_STATUS: ConvertedImageStatusResult = {
	isAnyConverting: false,
	isConverting: () => false,
	getConvertedUrl: () => null,
};

/**
 * Subscribe to the conversion status of some originals.
 *
 * All the work happens in `convertedImageStatusStore`, which runs ONE poll for the whole page and
 * caches answers outside React — so N images cost one request rather than N, and a remount reads
 * what is already known instead of refetching and flashing a placeholder. This hook is only the
 * React binding. An empty list subscribes to nothing and issues no request.
 */
export const useConvertedImageStatus = (
	pendingOriginals: PendingOriginal[]
): ConvertedImageStatusResult => {
	const originalsKey = (pendingOriginals || []).map(getOriginalKey).sort().join(',');
	// The public request portals reach this with no session, and the lookup is a SecuredAction —
	// subscribing there would spend the polling budget on 401s before the local decode starts.
	const canLookup = canLookupConvertedImages();
	// Re-subscribe only when the set actually changes, not on every render.
	const originals = useMemo(
		() => (canLookup ? pendingOriginals || [] : []),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[originalsKey, canLookup]
	);
	const [storeVersion, setStoreVersion] = useState(getStoreVersion);

	useEffect(() => {
		if (originals.length === 0) {
			return undefined;
		}
		return subscribeToConvertedImageStatus(originals, () => setStoreVersion(getStoreVersion()));
	}, [originals]);

	return useMemo(
		() =>
			canLookup
				? {
						isAnyConverting: originals.some((original) => isStillConverting(original)),
						isConverting: (original: PendingOriginal | null) => isStillConverting(original),
						getConvertedUrl: (original: PendingOriginal | null) => {
							const ref = findStoredRef(original);
							if (!ref || ref.status !== CONVERTED_IMAGE_STATUS_READY) {
								return null;
							}
							return ref.convertedUrl || null;
						},
				  }
				: INERT_STATUS,
		// storeVersion looks unused because the reads below go to the external store, not to React
		// state — it IS the invalidation signal, so it has to stay in the dependency list.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[originals, storeVersion, canLookup]
	);
};
